import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Layers, PanelsTopLeft, Sparkles, Wrench, HardHat, Truck } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { SERVICES } from "@/lib/constants";
import PageHero from "@/components/site/PageHero";
import CtaBanner from "@/components/site/CtaBanner";

const ICONS = { Building2, Layers, PanelsTopLeft, Sparkles, Wrench, HardHat, Truck };

export default function Leistungen() {
    const reduce = useReducedMotion();
    useSeo({
        title: "Leistungen – Reinigung in Innsbruck & Tirol | Alpen Reinigung",
        description:
            "Unsere Reinigungsleistungen in Innsbruck und Tirol: Gebäudereinigung, Fassadenreinigung, Fensterreinigung, Unterhaltsreinigung und Spezialreinigung. Detail-Seiten je Leistung.",
        path: "/leistungen",
    });

    return (
        <div>
            <PageHero
                eyebrow="Leistungen"
                title="Reinigungsleistungen in Innsbruck und Tirol"
                lead="Von der klassischen Gebäudereinigung bis zur Spezialreinigung im Tiroler Raum – finden Sie hier eine Übersicht unserer Kernbereiche und detaillierte Informationen je Leistung."
                breadcrumbs={[{ label: "Leistungen" }]}
            />
            <section className="relative pb-20 sm:pb-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="services-grid">
                        {SERVICES.map((s, i) => {
                            const Icon = ICONS[s.icon];
                            return (
                                <motion.article
                                    key={s.slug}
                                    initial={reduce ? false : { opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.45, delay: i * 0.05 }}
                                    className="group rounded-2xl border border-white/[0.10] bg-[rgba(18,27,42,0.55)] backdrop-blur-md overflow-hidden hover:border-[rgba(123,211,255,0.35)] transition-colors card-elevated"
                                    data-testid={`services-card-${s.slug}`}
                                >
                                    <div className="aspect-[5/3] relative overflow-hidden">
                                        <img
                                            src={s.image}
                                            alt={`${s.title} in Innsbruck und Tirol`}
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#07090C] via-[#07090C]/40 to-transparent" />
                                        <div className="absolute top-3 left-3">
                                            <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-[rgba(46,168,255,0.16)] border border-[rgba(46,168,255,0.35)] backdrop-blur">
                                                {Icon ? <Icon className="h-4 w-4 text-[#7BD3FF]" strokeWidth={1.7} /> : null}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h2 className="font-heading text-xl font-semibold text-white">{s.title}</h2>
                                        <p className="mt-2 text-sm text-[#C9D3E1] leading-relaxed">{s.short}</p>
                                        <Link to={`/leistungen/${s.slug}`} className="mt-5 inline-flex items-center gap-1 text-sm text-[#7BD3FF] hover:text-white">
                                            Mehr erfahren <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>
            </section>
            <CtaBanner />
        </div>
    );
}
